export const knownCurrencies = [
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'KRW',
  'CHF',
  'CAD',
  'AUD',
  'BRL',
  'SGD',
]

export type Token = { formatted: string; symbol: string }

export type Options = {
  /** Maximum fractional digits to show. */
  maxDecimals?: number | undefined
  /** Whether to show nonzero sub-cent fiat values with significant digits. */
  precise?: boolean | undefined
}

export type Formatted = {
  /** Primary display label. */
  label: string
  /** Full unrounded value for hover/title when the label is intentionally rounded. */
  title?: string | undefined
}

/** Format a token amount as fiat currency if the symbol contains a known currency, otherwise as crypto. */
export function fiat(token: Token, options: Options = {}): Formatted {
  const { maxDecimals, precise } = options
  const currency = inferCurrency(token.symbol)
  if (currency) {
    const n = Number.parseFloat(token.formatted)
    if (Number.isNaN(n)) return { label: token.formatted }
    if (precise && n > 0 && n < 0.01)
      return {
        label: new Intl.NumberFormat('en-US', {
          currency,
          maximumSignificantDigits: 1,
          minimumSignificantDigits: 1,
          style: 'currency',
        }).format(n),
        title: new Intl.NumberFormat('en-US', {
          currency,
          maximumFractionDigits: Math.min(token.formatted.split('.')[1]?.length ?? 0, 20),
          style: 'currency',
        }).format(n),
      }
    const fmt = new Intl.NumberFormat('en-US', {
      currency,
      maximumFractionDigits: maxDecimals,
      style: 'currency',
    })
    const label = fmt.format(n)
    // Show "$<0.01" instead of "$0.00" for nonzero sub-cent amounts
    const symbol = fmt.formatToParts(0).find((p) => p.type === 'currency')?.value ?? '$'
    if (n > 0 && n < 0.01 && !maxDecimals) return { label: `${symbol}<0.01`, title: fmt.format(n) }
    return { label }
  }
  return { label: crypto(token, options) }
}

/** Format a token amount with its symbol (e.g. "1,000.00 WETH"). */
export function crypto(token: Token, options: Options = {}): string {
  const { maxDecimals = 20 } = options
  const n = Number.parseFloat(token.formatted)
  const formatted = Number.isNaN(n)
    ? token.formatted
    : new Intl.NumberFormat('en-US', {
        maximumFractionDigits: maxDecimals,
        minimumFractionDigits: 2,
      }).format(n)
  return `${formatted} ${token.symbol}`
}

/** Infer an ISO 4217 currency code from a token symbol (e.g. "PathUSD" → "USD"). */
function inferCurrency(symbol: string): string | undefined {
  const upper = symbol.toUpperCase()
  return knownCurrencies.find((c) => upper.includes(c))
}
